import { createAppError } from '../../api/errors';
import type { MutationResult } from '../../api/contract';
import type { WithdrawalControlsRepository } from './repository';
import type {
  PartnerWithdrawalPolicy,
  PartnerWithdrawalVelocityRule,
  WithdrawalPolicyListQuery,
  WithdrawalVelocityListQuery,
} from './types';

const partnerId = '00000000-0000-4000-8000-000000000101';
const actorId = '00000000-0000-4000-8000-000000000001';
const timestamp = '2026-07-22T12:00:00+07:00';

export const withdrawalPolicyFixtures: readonly PartnerWithdrawalPolicy[] = Object.freeze([
  Object.freeze({
    id: '00000000-0000-4000-8000-000000001401',
    partnerId,
    currencyCode: 'THB',
    status: 'SUSPENDED',
    minimumAmount: '500.000000',
    maximumAmount: '100000.000000',
    dailyAmountLimit: '250000.000000',
    dailyCountLimit: 10,
    createdAt: timestamp,
    createdBy: actorId,
    updatedAt: timestamp,
    updatedBy: actorId,
  }),
]);

export const withdrawalVelocityFixtures: readonly PartnerWithdrawalVelocityRule[] = Object.freeze(
  (['PARTNER', 'MEMBER', 'CREDENTIAL', 'IP'] as const).map((dimension, index) =>
    Object.freeze({
      id: `00000000-0000-4000-8000-${String(1411 + index).padStart(12, '0')}`,
      partnerId,
      currencyCode: 'THB',
      dimension,
      windowSeconds: 86400,
      maximumCount: dimension === 'PARTNER' ? 100 : 5,
      maximumAmount: dimension === 'PARTNER' ? '250000.000000' : '50000.000000',
      status: 'ACTIVE' as const,
      createdAt: timestamp,
      createdBy: actorId,
      updatedAt: timestamp,
      updatedBy: actorId,
    }),
  ),
);

export class MockWithdrawalControlsRepository implements WithdrawalControlsRepository {
  private policies = withdrawalPolicyFixtures.map((item) => ({ ...item }));
  private velocityRules = withdrawalVelocityFixtures.map((item) => ({ ...item }));
  private sequence = 1500;

  async listPolicies(partner: string, query: WithdrawalPolicyListQuery, signal: AbortSignal) {
    throwIfAborted(signal);
    const items = this.policies.filter(
      (item) =>
        item.partnerId === partner &&
        (query.filters.status === '' || item.status === query.filters.status) &&
        (query.filters.currencyCode.trim() === '' ||
          item.currencyCode === query.filters.currencyCode.trim().toUpperCase()),
    );
    return page(items, query.page, query.pageSize, 'mock-withdrawal-policies');
  }

  async createPolicy(
    partner: string,
    input: Parameters<WithdrawalControlsRepository['createPolicy']>[1],
  ) {
    const created: PartnerWithdrawalPolicy = {
      id: this.nextId(),
      partnerId: partner,
      currencyCode: input.currencyCode.toUpperCase(),
      status: 'SUSPENDED',
      minimumAmount: fixedDecimal(input.minimumAmount),
      maximumAmount: fixedDecimal(input.maximumAmount),
      dailyAmountLimit: null,
      dailyCountLimit: null,
      createdAt: timestamp,
      createdBy: actorId,
      updatedAt: timestamp,
      updatedBy: actorId,
    };
    this.policies = [...this.policies, created];
    return result(created, 'mock-policy-create');
  }

  async getPolicy(partner: string, policy: string, signal: AbortSignal) {
    throwIfAborted(signal);
    return { ...this.findPolicy(partner, policy) };
  }

  async updatePolicy(
    partner: string,
    policyId: string,
    input: Parameters<WithdrawalControlsRepository['updatePolicy']>[2],
  ) {
    const current = this.findPolicy(partner, policyId);
    const updated = {
      ...current,
      ...(input.minimumAmount === undefined
        ? {}
        : { minimumAmount: fixedDecimal(input.minimumAmount) }),
      ...(input.maximumAmount === undefined
        ? {}
        : { maximumAmount: fixedDecimal(input.maximumAmount) }),
      updatedAt: timestamp,
      updatedBy: actorId,
    };
    this.policies = replace(this.policies, updated);
    return result(updated, 'mock-policy-update');
  }

  async changePolicyStatus(
    partner: string,
    policyId: string,
    input: Parameters<WithdrawalControlsRepository['changePolicyStatus']>[2],
  ) {
    const current = this.findPolicy(partner, policyId);
    if (current.status === input.status) return result({ ...current }, 'mock-policy-status-noop');
    const updated = { ...current, status: input.status, updatedAt: timestamp, updatedBy: actorId };
    this.policies = replace(this.policies, updated);
    return result(updated, 'mock-policy-status');
  }

  async listVelocityRules(
    partner: string,
    query: WithdrawalVelocityListQuery,
    signal: AbortSignal,
  ) {
    throwIfAborted(signal);
    const items = this.velocityRules.filter(
      (item) =>
        item.partnerId === partner &&
        (query.filters.status === '' || item.status === query.filters.status) &&
        (query.filters.currencyCode.trim() === '' ||
          item.currencyCode === query.filters.currencyCode.trim().toUpperCase()) &&
        (query.filters.dimension === '' || item.dimension === query.filters.dimension),
    );
    return page(items, query.page, query.pageSize, 'mock-withdrawal-velocity');
  }

  async getVelocityRule(partner: string, rule: string, signal: AbortSignal) {
    throwIfAborted(signal);
    return { ...this.findRule(partner, rule) };
  }

  private findPolicy(partner: string, policyId: string) {
    const item = this.policies.find(
      (candidate) => candidate.partnerId === partner && candidate.id === policyId,
    );
    if (item === undefined) throw notFound('Withdrawal policy', 'mock-policy-not-found');
    return item;
  }

  private findRule(partner: string, ruleId: string) {
    const item = this.velocityRules.find(
      (candidate) => candidate.partnerId === partner && candidate.id === ruleId,
    );
    if (item === undefined) throw notFound('Velocity rule', 'mock-rule-not-found');
    return item;
  }

  private nextId() {
    this.sequence += 1;
    return `00000000-0000-4000-8000-${String(this.sequence).padStart(12, '0')}`;
  }
}

function page<T>(items: readonly T[], pageNumber: number, pageSize: number, requestId: string) {
  const start = Math.max(0, pageNumber - 1) * pageSize;
  return { items: items.slice(start, start + pageSize), total: items.length, requestId };
}

function fixedDecimal(value: string): string {
  const match = /^(\d+)(?:\.(\d{1,6}))?$/u.exec(value.trim());
  if (match === null)
    throw new RangeError('A non-negative decimal with up to six fractional digits is required');
  return `${match[1]}.${(match[2] ?? '').padEnd(6, '0')}`;
}

function replace<T extends { readonly id: string }>(items: readonly T[], value: T): T[] {
  return items.map((item) => (item.id === value.id ? value : item));
}

function result<T>(data: T, requestId: string): MutationResult<T> {
  return { data: { ...data }, requestId };
}

function throwIfAborted(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Withdrawal controls request aborted', 'AbortError');
}

function notFound(message: string, requestId: string) {
  return createAppError({
    status: 404,
    code: 'WITHDRAWAL_CONTROL_NOT_FOUND',
    message,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}
